import { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';

export interface UserInfo {
  id: string;
  email: string;
  name: string;
  first_name?: string;
  last_name?: string;
  roles: string[];
  permissions: string[];
  department?: string;
  job_title?: string;
  status: string;
}

export const useUserInfo = () => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user has super-admin role
  const isSuperAdmin = userInfo?.roles?.includes('super-admin') || false;

  // Check if user has admin role (includes super-admin)
  const isAdmin = userInfo?.roles?.some(role => ['admin', 'super-admin'].includes(role)) || false;

  // Check if user has any content management role (admin, content-owner, content-member)
  const hasContentAccess = userInfo?.roles?.some(role => 
    ['admin', 'super-admin', 'content-owner', 'content-member'].includes(role)
  ) || false;

  // Fetch user info
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await apiService.get<UserInfo>('/api/users/me');
        setUserInfo(response);
      } catch (error) {
        console.error('Failed to fetch user info:', error);
        setError('Failed to fetch user information');
        // Set fallback - assume regular admin if API fails
        setUserInfo({
          id: 'unknown',
          email: 'unknown@example.com',
          name: 'Unknown User',
          first_name: 'Unknown',
          last_name: 'User',
          roles: ['admin'],
          permissions: [],
          status: 'active'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserInfo();
  }, []);

  return {
    userInfo,
    isLoading,
    error,
    isSuperAdmin,
    isAdmin,
    hasContentAccess,
  };
};